import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, type EditTxBody } from '../lib/api';
import { formatDateTime, formatVnd, formatAmountOriginal } from '../lib/format';
import { AssignDropdown } from '../components/AssignDropdown';
import { CommentsPanel } from '../components/CommentsPanel';
import { AttachmentsPanel } from '../components/AttachmentsPanel';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';

const TX_TYPE_VARIANT = {
  purchase: 'default',
  refund: 'info',
  fee: 'warning',
  decline: 'destructive',
} as const;
const TX_STATUS_VARIANT: Record<
  string,
  'default' | 'success' | 'warning' | 'destructive' | 'info'
> = {
  assigned: 'success',
  unassigned: 'warning',
  voided: 'destructive',
};

export function TransactionDetailPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const id = idParam ? parseInt(idParam, 10) : NaN;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['tx', id],
    queryFn: () => api.getTransaction(id),
    enabled: Number.isFinite(id),
  });
  const { data: sug } = useQuery({
    queryKey: ['suggestions', id],
    queryFn: () => api.getSuggestions(id),
    enabled: data?.transaction.status === 'unassigned',
  });
  const { data: deptsData } = useQuery({
    queryKey: ['departments-lookup'],
    queryFn: () => api.listDepartments(),
    staleTime: 5 * 60_000,
  });

  const assign = useMutation({
    mutationFn: (department_id: number) => api.assignTransaction(id, department_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tx', id] });
      qc.invalidateQueries({ queryKey: ['pending'] });
      qc.invalidateQueries({ queryKey: ['txs'] });
    },
  });
  const unassign = useMutation({
    mutationFn: () => api.unassignTransaction(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tx', id] });
      qc.invalidateQueries({ queryKey: ['txs'] });
    },
  });
  const [editing, setEditing] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  useEffect(() => {
    if (savedAt == null) return;
    const t = window.setTimeout(() => setSavedAt(null), 3000);
    return () => window.clearTimeout(t);
  }, [savedAt]);

  const edit = useMutation({
    mutationFn: (body: EditTxBody) => api.editTransaction(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tx', id] });
      qc.invalidateQueries({ queryKey: ['txs'] });
      qc.invalidateQueries({ queryKey: ['pending'] });
      qc.invalidateQueries({ queryKey: ['kpis'] });
      setEditing(false);
      setSavedAt(Date.now());
    },
  });

  const ackMut = useMutation({
    mutationFn: (alertId: number) => api.acknowledgeAlert(alertId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tx', id] });
      qc.invalidateQueries({ queryKey: ['alerts-count'] });
      qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
  const dismissMut = useMutation({
    mutationFn: (alertId: number) => api.dismissAlert(alertId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tx', id] });
      qc.invalidateQueries({ queryKey: ['alerts-count'] });
      qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  const [rawOpen, setRawOpen] = useState(false);

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => (history.length > 1 ? navigate(-1) : navigate('/transactions'))}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-card hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <ArrowLeft size={16} strokeWidth={1.5} />
          Back
        </button>
        <Link to="/transactions" className="text-sm text-primary hover:underline">
          All transactions →
        </Link>
      </div>

      <h2 className="font-heading text-xl font-semibold text-foreground">Transaction #{id}</h2>

      {isLoading && <Skeleton className="h-60 w-full" />}
      {error && (
        <div className="rounded border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          Error: {String(error)}
        </div>
      )}
      {data && (
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-card p-5 text-sm">
            <dl className="grid grid-cols-1 gap-y-3 sm:grid-cols-[140px_1fr]">
              <dt className="text-muted-foreground">Date</dt>
              <dd className="text-foreground">{formatDateTime(data.transaction.occurred_at)}</dd>
              <dt className="text-muted-foreground">Card</dt>
              <dd className="text-foreground">
                {data.transaction.card_alias} ({data.transaction.bank_code})
              </dd>
              <dt className="text-muted-foreground">Merchant</dt>
              <dd>
                <div className="text-foreground">
                  {data.transaction.merchant_canonical ?? data.transaction.raw_merchant_text}
                </div>
                {data.transaction.merchant_canonical &&
                  data.transaction.merchant_canonical !== data.transaction.raw_merchant_text && (
                    <div className="text-xs text-muted-foreground/70">
                      raw: {data.transaction.raw_merchant_text}
                    </div>
                  )}
              </dd>
              <dt className="text-muted-foreground">Amount</dt>
              <dd>
                <span className="tabular-nums text-foreground">
                  {formatAmountOriginal(
                    data.transaction.amount_original,
                    data.transaction.currency,
                  )}
                </span>
                {data.transaction.currency !== 'VND' && (
                  <span className="text-muted-foreground">
                    {' '}
                    · <span className="tabular-nums">
                      {formatVnd(data.transaction.amount_vnd)}
                    </span>{' '}
                    @ {data.transaction.fx_rate}
                  </span>
                )}
              </dd>
              <dt className="text-muted-foreground">Type</dt>
              <dd>
                <Badge variant={TX_TYPE_VARIANT[data.transaction.type]}>
                  {data.transaction.type}
                </Badge>
              </dd>
              <dt className="text-muted-foreground">Status</dt>
              <dd>
                <Badge variant={TX_STATUS_VARIANT[data.transaction.status] ?? 'default'}>
                  {data.transaction.status}
                </Badge>
              </dd>
            </dl>
          </div>

          {data.alerts.length > 0 && (
            <div className="space-y-2">
              {data.alerts.map((al) => {
                const sevVariant =
                  al.severity === 'high'
                    ? 'destructive'
                    : al.severity === 'medium'
                      ? 'warning'
                      : 'info';
                const borderBg =
                  al.severity === 'high'
                    ? 'border-destructive/30 bg-destructive/10'
                    : al.severity === 'medium'
                      ? 'border-warning/30 bg-warning/10'
                      : 'border-border bg-card';
                return (
                  <div key={al.id} className={`rounded border p-3 text-sm ${borderBg}`}>
                    <div className="mb-1 flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Badge variant={sevVariant}>{al.severity}</Badge>
                        <span className="font-mono text-xs text-muted-foreground">
                          {al.rule_code}
                        </span>
                      </span>
                      <span className="text-xs text-muted-foreground/70">{al.status}</span>
                    </div>
                    <div className="text-foreground">{al.summary}</div>
                    {al.status === 'open' && (
                      <div className="mt-2 flex gap-2">
                        <Button
                          variant="secondary"
                          onClick={() => ackMut.mutate(al.id)}
                          disabled={ackMut.isPending}
                          className="border-success/40 text-success hover:bg-success/10"
                        >
                          Acknowledge
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => dismissMut.mutate(al.id)}
                          disabled={dismissMut.isPending}
                        >
                          Dismiss
                        </Button>
                      </div>
                    )}
                    {al.status === 'acknowledged' && (
                      <div className="mt-2 flex items-center justify-between text-xs">
                        <span className="text-success">✓ Acknowledged</span>
                        <Button
                          variant="secondary"
                          onClick={() => dismissMut.mutate(al.id)}
                          disabled={dismissMut.isPending}
                        >
                          Dismiss
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {data.transaction.status === 'unassigned' && (
            <div className="rounded-lg border border-border bg-card p-4 text-sm">
              <div className="mb-2 font-medium text-muted-foreground">Assign department</div>
              <AssignDropdown
                suggestions={sug?.suggestions ?? []}
                onAssign={(dept_id) => assign.mutate(dept_id)}
                disabled={assign.isPending}
              />
            </div>
          )}
          {data.transaction.status === 'assigned' && !editing && (
            <div className="rounded-lg border border-success/30 bg-success/10 p-4 text-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="text-foreground">
                  Assigned to{' '}
                  <span className="font-mono">{data.transaction.department_code ?? '?'}</span>
                  {data.transaction.department_name && (
                    <span className="text-muted-foreground">
                      {' '}
                      — {data.transaction.department_name}
                    </span>
                  )}
                </div>
                {savedAt != null && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded bg-success/10 px-2 py-0.5 text-xs text-success">
                    <CheckCircle2 size={12} strokeWidth={1.5} /> Đã cập nhật
                  </span>
                )}
              </div>
              <div className="mt-2 flex gap-2">
                <Button variant="ghost" onClick={() => setEditing(true)} className="text-primary">
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => unassign.mutate()}
                  disabled={unassign.isPending}
                  className="text-destructive hover:text-destructive"
                >
                  Unassign
                </Button>
              </div>
            </div>
          )}
          {editing && data.transaction.status === 'assigned' && (
            <EditPanel
              initialDept={data.transaction.department_id}
              initialMerchant={data.transaction.raw_merchant_text}
              depts={deptsData?.rows ?? []}
              pending={edit.isPending}
              onCancel={() => setEditing(false)}
              onSave={(body) => edit.mutate(body)}
            />
          )}

          <CommentsPanel transactionId={id} />
          <AttachmentsPanel transactionId={id} />
          <div>
            <Button variant="ghost" onClick={() => setRawOpen((v) => !v)} className="text-primary">
              {rawOpen ? 'Hide' : 'Show'} raw email
            </Button>
            {rawOpen && (
              <pre className="mt-2 max-h-60 overflow-auto whitespace-pre-wrap rounded bg-card p-3 text-xs text-muted-foreground">
                {data.raw_email_body}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function EditPanel({
  initialDept,
  initialMerchant,
  depts,
  pending,
  onCancel,
  onSave,
}: {
  initialDept: number | null;
  initialMerchant: string;
  depts: { id: number; code: string; name: string; deleted_at: string | null }[];
  pending: boolean;
  onCancel: () => void;
  onSave: (body: EditTxBody) => void;
}) {
  const [deptValue, setDeptValue] = useState<string>(
    initialDept != null ? String(initialDept) : '',
  );
  const [merchant, setMerchant] = useState<string>(initialMerchant);

  function handleSave() {
    const body: EditTxBody = {};
    const newDeptId = deptValue === '' ? null : Number(deptValue);
    if (newDeptId !== initialDept) body.department_id = newDeptId;
    if (merchant.trim() !== initialMerchant && merchant.trim().length > 0) {
      body.raw_merchant_text = merchant.trim();
    }
    if (Object.keys(body).length === 0) {
      onCancel();
      return;
    }
    onSave(body);
  }

  return (
    <div className="rounded-lg border border-primary/30 bg-card p-4 text-sm">
      <div className="mb-2 text-muted-foreground">Chỉnh sửa</div>
      <label className="mb-2 block">
        <span className="mb-1 block text-xs text-muted-foreground">Department</span>
        <select
          value={deptValue}
          onChange={(e) => setDeptValue(e.target.value)}
          className="w-full rounded border border-border bg-background px-2 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="">(Unassign)</option>
          {depts
            .filter((d) => !d.deleted_at)
            .map((d) => (
              <option key={d.id} value={d.id}>
                {d.code} — {d.name}
              </option>
            ))}
        </select>
      </label>
      <label className="mb-3 block">
        <span className="mb-1 block text-xs text-muted-foreground">Merchant text</span>
        <input
          type="text"
          value={merchant}
          onChange={(e) => setMerchant(e.target.value)}
          className="w-full rounded border border-border bg-background px-2 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          placeholder="Tên merchant"
        />
      </label>
      <div className="flex gap-2">
        <Button variant="default" onClick={handleSave} disabled={pending}>
          {pending ? 'Đang lưu…' : 'Save'}
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
